import * as React from 'react';
import {
  ArrowRight,
  BookOpen,
  FileText,
  User,
  Users,
  Settings,
  PlusIcon,
  Wallet,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';

import { NavMain } from '@/components/layout/nav-main';
import { NavUser } from '@/components/layout/nav-user';
import { OrganizationSwitcher } from '@/components/layout/organization-switcher';
import { Button } from '@/components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarGroup,
} from '@/components/ui/sidebar';
import { useAuthClient } from '@/lib/authClient';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useSidebarProposalBadges } from '@/hooks/use-sidebar-proposal-badges';

// Application data
const data = {
  navMain: [
    {
      title: 'Transactions',
      url: '/transactions',
      icon: ArrowRight,
      isActive: false,
      items: [],
    },
    {
      title: 'Accounts',
      url: '/accounts',
      icon: Wallet,
      isActive: false,
      items: [],
    },
    {
      title: 'Policies',
      url: '/policies',
      icon: FileText,
      isActive: false,
      items: [],
    },
    {
      title: 'Groups',
      url: '/groups',
      icon: Users,
      isActive: false,
      items: [],
    },
    {
      title: 'Members',
      url: '/members',
      icon: User,
      isActive: false,
      items: [],
    },
    {
      title: 'Address Book',
      url: '/address-book',
      icon: BookOpen,
      isActive: false,
      items: [],
    },
    {
      title: 'Settings',
      url: '/settings/organization',
      icon: Settings,
      isActive: false,
      items: [],
    },
  ],
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const { user } = useAuthClient();
  const { organization } = useMyOrganization();
  const navigate = useNavigate();
  const proposalBadges = useSidebarProposalBadges();

  // Update active state based on current path
  const navMainWithActiveState = data.navMain.map((item) => ({
    ...item,
    isActive: location.pathname.startsWith(item.url),
    hasPendingProposals: proposalBadges[item.url] ?? false,
  }));

  if (!user) throw new Error('User not found');
  if (!organization) return <div>Loading...</div>;

  return (
    <Sidebar
      collapsible="none"
      className="bg-sidebar border-r border-border"
      {...props}
    >
      <SidebarHeader>
        <OrganizationSwitcher organizations={[organization]} />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <Button
            className="mx-3 mb-2 gap-2 hover:border-primary focus:outline-sidebar-ring"
            size="sm"
            onClick={() => navigate('/new-transaction')}
          >
            <PlusIcon className="h-4 w-4" />
            <span>New Transaction</span>
          </Button>
        </SidebarGroup>
        <NavMain items={navMainWithActiveState} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={user} />
      </SidebarFooter>
    </Sidebar>
  );
}
